import { Button, Spinner, Table } from 'react-bootstrap';
import { CopyButton } from '@/components/ui/CopyButton';
import type { DeploymentLockRecord } from '@/services/deploymentService';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import './deployments.css';

interface LocksTableProps {
  locks: DeploymentLockRecord[];
  loading: boolean;
  /** The lock being released right now. */
  releasingId: string | null;
  onRelease: (lock: DeploymentLockRecord) => void;
}

/** The clients held by a deploy. A lock left behind by a dead run blocks the client until somebody releases it. */
export function LocksTable({ locks, loading, releasingId, onRelease }: LocksTableProps) {
  const sorted = [...locks].sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  return (
    <Table hover responsive className="mb-0 deploy-table">
      <thead>
        <tr>
          <th>Client</th>
          <th>Held since</th>
          <th>Lock ID</th>
          <th aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {loading && locks.length === 0 && (
          <tr>
            <td colSpan={4} className="deploy-empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Looking for locks
            </td>
          </tr>
        )}
        {!loading && locks.length === 0 && (
          <tr>
            <td colSpan={4} className="deploy-empty">
              No client is locked.
            </td>
          </tr>
        )}
        {sorted.map((lock) => (
          <tr key={lock.deploymentId}>
            <td>
              <span className="deploy-client">{lock.clientName}</span>
            </td>
            <td>
              <div>{timeAgo(lock.startedAt) || 'Unknown'}</div>
              <div className="deploy-sub">{formatNzDateTime(lock.startedAt)}</div>
            </td>
            <td>
              <span className="mono">{lock.deploymentId}</span>
              <CopyButton value={lock.deploymentId} label="Copy lock ID" />
            </td>
            <td className="deploy-actions">
              <Button
                variant="outline-danger"
                size="sm"
                disabled={releasingId === lock.deploymentId}
                onClick={() => onRelease(lock)}
              >
                {releasingId === lock.deploymentId && <Spinner size="sm" className="me-1" />}
                Release lock
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default LocksTable;
